import type { Map as MapaMapLibre } from 'maplibre-gl'
import { createContext, type Dispatch, type RefObject, useContext } from 'react'

import type { CamadaDoCatalogo } from '@/components/mapa/catalogo'
import { type IdMapaBase, MAPA_BASE_INICIAL } from '@/components/mapa/mapasBase'

/*
 * O estado do mapa num lugar só: o que a pessoa escolheu para ver. O painel de
 * camadas, a gaveta, a barra do mapa e o próprio mapa leem e mudam este
 * estado, e o mapa o traduz em estilo do MapLibre (estiloDoMapa.ts).
 *
 * O estado não persiste: recarregar a página volta ao inicial. Escolha que
 * entrar no mapa (camadas ligadas, por exemplo) entra aqui, como campo e ação
 * novos — e não como `useState` no componente que mostra o controle, porque o
 * mesmo controle aparece no painel fixo e na gaveta.
 */

/** Opacidade das camadas temáticas, em porcentagem: de 20 a 100, de 5 em 5, começando em 80. */
export const OPACIDADE = { minima: 20, maxima: 100, passo: 5, inicial: 80 } as const

/**
 * As camadas temáticas ligadas. A lista guarda a camada do catálogo inteira, e
 * não só o código: o estilo do mapa sai do estado sozinho, sem o mapa ter de
 * consultar o catálogo. A ordem é a em que foram ligadas — com mais de uma
 * variável, a última ligada pinta o contorno do imóvel.
 */
export interface CamadasLigadas {
  ligadas: readonly CamadaDoCatalogo[]
  /** Os códigos dos biomas ligados entre as subcamadas de Biomas. */
  biomas: readonly number[]
}

/**
 * Quais imóveis as camadas de imóvel mostram por causa da busca: todos
 * (`nenhum`, sem busca), os de um município, só um recibo, ou nenhum (`vazio`:
 * município fora da versão, recibo fora da versão ou inexistente). Vem da
 * busca, não de uma escolha no painel; por isso o painel não o altera.
 */
export type FiltroDaBusca =
  | { tipo: 'nenhum' }
  | { tipo: 'municipio'; codigo: number }
  | { tipo: 'recibo'; recibo: string }
  | { tipo: 'vazio' }

export interface EstadoDoMapa {
  mapaBase: IdMapaBase
  /** Porcentagem aplicada às camadas temáticas; o mapa-base fica sempre inteiro. */
  opacidade: number
  camadas: CamadasLigadas
  filtroDaBusca: FiltroDaBusca
}

const NENHUMA_CAMADA: CamadasLigadas = { ligadas: [], biomas: [] }

export const ESTADO_INICIAL_DO_MAPA: EstadoDoMapa = {
  mapaBase: MAPA_BASE_INICIAL,
  opacidade: OPACIDADE.inicial,
  camadas: NENHUMA_CAMADA,
  filtroDaBusca: { tipo: 'nenhum' },
}

export type AcaoDoMapa =
  | { tipo: 'escolherMapaBase'; mapaBase: IdMapaBase }
  | { tipo: 'mudarOpacidade'; opacidade: number }
  | { tipo: 'ligarCamada'; camada: CamadaDoCatalogo }
  | { tipo: 'desligarCamada'; codigo: string }
  | { tipo: 'alternarSubcamada'; camada: CamadaDoCatalogo; subcamada: number; ligada: boolean }
  | { tipo: 'selecionarTodas'; camadas: readonly CamadaDoCatalogo[] }
  | { tipo: 'limparSelecao' }
  | { tipo: 'definirFiltroDaBusca'; filtroDaBusca: FiltroDaBusca }

/**
 * Leva qualquer número ao degrau válido mais próximo, dentro dos limites: o
 * controle já só produz degraus, mas o estado não confia em quem o chama.
 */
function opacidadeValida(valor: number): number {
  if (!Number.isFinite(valor)) return OPACIDADE.inicial
  const degrau = Math.round(valor / OPACIDADE.passo) * OPACIDADE.passo
  return Math.min(OPACIDADE.maxima, Math.max(OPACIDADE.minima, degrau))
}

/**
 * Liga uma camada. Camada não entregue não entra nunca, venha o pedido de onde
 * vier: o mapa não inventa o dado que o IIS ainda não mandou. Ligar a camada
 * de biomas liga todos os biomas dela.
 */
function ligar(camadas: CamadasLigadas, camada: CamadaDoCatalogo): CamadasLigadas {
  if (!camada.entregue || camadas.ligadas.some((c) => c.codigo === camada.codigo)) return camadas
  return {
    ligadas: [...camadas.ligadas, camada],
    biomas: camada.subcamadas.length > 0 ? camada.subcamadas.map((s) => s.codigo) : camadas.biomas,
  }
}

function desligar(camadas: CamadasLigadas, codigo: string): CamadasLigadas {
  const camada = camadas.ligadas.find((c) => c.codigo === codigo)
  if (camada === undefined) return camadas
  return {
    ligadas: camadas.ligadas.filter((c) => c.codigo !== codigo),
    biomas: camada.subcamadas.length > 0 ? [] : camadas.biomas,
  }
}

/**
 * Liga ou desliga um bioma. Ligar um bioma liga a camada dele; desligar o
 * último desliga a camada, para que não fique marcada uma camada que não
 * pinta nada.
 */
function alternarSubcamada(
  camadas: CamadasLigadas,
  camada: CamadaDoCatalogo,
  subcamada: number,
  ligada: boolean
): CamadasLigadas {
  if (!camada.entregue || !camada.subcamadas.some((s) => s.codigo === subcamada)) return camadas
  if (camadas.biomas.includes(subcamada) === ligada) return camadas
  if (!ligada) {
    const restantes = camadas.biomas.filter((codigo) => codigo !== subcamada)
    return restantes.length === 0
      ? desligar(camadas, camada.codigo)
      : { ...camadas, biomas: restantes }
  }
  const ligadas = camadas.ligadas.some((c) => c.codigo === camada.codigo)
    ? camadas.ligadas
    : [...camadas.ligadas, camada]
  // Os biomas ficam na ordem do catálogo, não na do clique.
  const biomas = camada.subcamadas
    .map((s) => s.codigo)
    .filter((codigo) => codigo === subcamada || camadas.biomas.includes(codigo))
  return { ligadas, biomas }
}

/**
 * "Selecionar todas": liga as camadas entregues, na ordem do catálogo, e todos
 * os biomas; as que já estavam ligadas ficam onde estavam. A não entregue fica
 * de fora sem aviso — o aviso é para quem clica na caixa dela.
 */
function selecionarTodas(
  camadas: CamadasLigadas,
  catalogo: readonly CamadaDoCatalogo[]
): CamadasLigadas {
  const todas = catalogo.reduce(ligar, camadas)
  const biomas = catalogo
    .filter((camada) => camada.entregue)
    .flatMap((camada) => camada.subcamadas.map((s) => s.codigo))
  return todas === camadas && biomas.every((codigo) => camadas.biomas.includes(codigo))
    ? camadas
    : { ...todas, biomas }
}

/** Dois filtros da busca dizem o mesmo? Comparado pelo conteúdo, para repetir o filtro não renderizar de novo. */
function mesmoFiltroDaBusca(a: FiltroDaBusca, b: FiltroDaBusca): boolean {
  if (a.tipo !== b.tipo) return false
  if (a.tipo === 'municipio' && b.tipo === 'municipio') return a.codigo === b.codigo
  if (a.tipo === 'recibo' && b.tipo === 'recibo') return a.recibo === b.recibo
  return true
}

export function reduzirEstadoDoMapa(estado: EstadoDoMapa, acao: AcaoDoMapa): EstadoDoMapa {
  const comCamadas = (camadas: CamadasLigadas): EstadoDoMapa =>
    camadas === estado.camadas ? estado : { ...estado, camadas }

  switch (acao.tipo) {
    case 'escolherMapaBase':
      return estado.mapaBase === acao.mapaBase ? estado : { ...estado, mapaBase: acao.mapaBase }
    case 'mudarOpacidade': {
      const opacidade = opacidadeValida(acao.opacidade)
      return estado.opacidade === opacidade ? estado : { ...estado, opacidade }
    }
    case 'ligarCamada':
      return comCamadas(ligar(estado.camadas, acao.camada))
    case 'desligarCamada':
      return comCamadas(desligar(estado.camadas, acao.codigo))
    case 'alternarSubcamada':
      return comCamadas(alternarSubcamada(estado.camadas, acao.camada, acao.subcamada, acao.ligada))
    case 'selecionarTodas':
      return comCamadas(selecionarTodas(estado.camadas, acao.camadas))
    case 'limparSelecao':
      return estado.camadas.ligadas.length === 0 ? estado : { ...estado, camadas: NENHUMA_CAMADA }
    case 'definirFiltroDaBusca':
      return mesmoFiltroDaBusca(estado.filtroDaBusca, acao.filtroDaBusca)
        ? estado
        : { ...estado, filtroDaBusca: acao.filtroDaBusca }
  }
}

/**
 * O filtro da busca que as camadas de imóvel recebem de fato. Com a comparação
 * aberta, a busca de um recibo deixa de esconder os demais imóveis: o cartão da
 * comparação pede um clique em outro imóvel, e o clique só alcança o que está
 * desenhado. A câmera já foi ao recibo buscado, e o destaque continua nele.
 * Os outros filtros não mudam — o recibo de fora ou inexistente continua sem
 * imóvel nenhum no lugar.
 */
export function filtroNoMapa(filtroDaBusca: FiltroDaBusca, comparando: boolean): FiltroDaBusca {
  return comparando && filtroDaBusca.tipo === 'recibo' ? { tipo: 'nenhum' } : filtroDaBusca
}

export interface ContextoDoMapa {
  estado: EstadoDoMapa
  despachar: Dispatch<AcaoDoMapa>
  /**
   * A instância do MapLibre, enquanto o mapa estiver montado. É para comandos
   * que movem a câmera ("Brasil"), e não para mudar o estilo: o estilo sai do
   * estado.
   */
  mapa: RefObject<MapaMapLibre | null>
  /** A área do mapa (mapa, barra, legenda): é ela que ocupa a tela em "Tela cheia". */
  area: RefObject<HTMLDivElement | null>
}

export const ContextoDoMapa = createContext<ContextoDoMapa | null>(null)

export function useEstadoDoMapa(): ContextoDoMapa {
  const contexto = useContext(ContextoDoMapa)
  if (contexto === null) {
    throw new Error('useEstadoDoMapa precisa de um EstadoDoMapaProvider acima na árvore')
  }
  return contexto
}
